import { iseeInstance } from '../axiosService'
import { getJWT } from '../../utils/functions'

class CustomAgentService {
  async getAgents (portalName) {
    return iseeInstance.get(`/custom-agents?portal=${encodeURIComponent(portalName)}`)
  }

  async getAgent (agentId) {
    return iseeInstance.get(`/custom-agents/${agentId}`)
  }

  async createAgent (data) {
    return iseeInstance.post('/custom-agents', data)
  }

  async updateAgent (agentId, data) {
    return iseeInstance.put(`/custom-agents/${agentId}`, data)
  }

  async deleteAgent (agentId) {
    return iseeInstance.delete(`/custom-agents/${agentId}`)
  }

  async uploadDocuments (agentId, files) {
    const formData = new FormData()
    files.forEach(file => formData.append('documents', file))
    return iseeInstance.post(`/custom-agents/${agentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    })
  }

  async deleteDocument (agentId, documentId) {
    return iseeInstance.delete(`/custom-agents/${agentId}/documents/${documentId}`)
  }

  async getDocumentStatus (agentId, documentId) {
    return iseeInstance.get(`/custom-agents/${agentId}/documents/${documentId}/status`)
  }

  async getConversations (agentId) {
    return iseeInstance.get(`/custom-agents/${agentId}/conversations`)
  }

  async getConversation (agentId, conversationId) {
    return iseeInstance.get(`/custom-agents/${agentId}/conversations/${conversationId}`)
  }

  async sendMessage (agentId, message, conversationId) {
    return iseeInstance.post(`/custom-agents/${agentId}/chat`, {
      message,
      conversationId
    }, { timeout: 60000 })
  }

  /**
   * Send a message with SSE streaming.
   * Calls onToken for each text chunk and returns the final result.
   */
  async sendMessageStream (agentId, message, conversationId, { onToken, onStart }) {
    const token = await getJWT()
    const baseURL = iseeInstance.defaults.baseURL
    const response = await fetch(`${baseURL}/custom-agents/${agentId}/chat?stream=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({ message, conversationId })
    })

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let result = { conversationId: null, answer: '', sources: [] }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() // keep incomplete line in buffer

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        try {
          const event = JSON.parse(line.slice(6))
          if (event.type === 'start') {
            result.conversationId = event.conversationId
            onStart?.(event.conversationId)
          } else if (event.type === 'token') {
            result.answer += event.content
            onToken?.(event.content)
          } else if (event.type === 'done') {
            result.sources = event.sources
          }
        } catch { /* ignore malformed lines */ }
      }
    }

    return result
  }

  async deleteConversation (agentId, conversationId) {
    return iseeInstance.delete(`/custom-agents/${agentId}/conversations/${conversationId}`)
  }
}

export default new CustomAgentService()
